import React, { useEffect, useMemo, useState } from 'react';
import ReservationQrCode from './ReservationQrCode';

function getDefaultPickupDate() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function buildInitialForm(equipmentId = '') {
  return {
    equipmentId,
    locationId: '',
    borrowerName: '',
    email: '',
    phone: '',
    preferredPickupDate: getDefaultPickupDate(),
  };
}

function formatDisplayDate(dateString) {
  if (!dateString) {
    return 'Not set';
  }

  return new Date(`${dateString}T12:00:00`).toLocaleDateString('en-CA', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export default function EquipmentReservationModal({
  open,
  equipmentCatalog,
  locations,
  inventory,
  initialEquipmentId,
  reservation,
  submitting,
  onClose,
  onSubmit,
}) {
  const [form, setForm] = useState(() => buildInitialForm(initialEquipmentId));
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) {
      return;
    }

    setForm(buildInitialForm(initialEquipmentId));
    setError('');
  }, [initialEquipmentId, open]);

  const equipmentById = useMemo(
    () => Object.fromEntries(equipmentCatalog.map((item) => [item.id, item])),
    [equipmentCatalog],
  );
  const locationById = useMemo(
    () => Object.fromEntries(locations.map((item) => [item.id, item])),
    [locations],
  );

  const availableLocations = useMemo(() => {
    if (!form.equipmentId) {
      return [];
    }

    return inventory
      .filter((item) => item.equipmentId === form.equipmentId && item.availableCount > 0)
      .map((item) => ({
        ...locationById[item.locationId],
        availableCount: item.availableCount,
      }))
      .filter(Boolean);
  }, [form.equipmentId, inventory, locationById]);

  useEffect(() => {
    if (!form.locationId) {
      return;
    }

    const stillAvailable = availableLocations.some((location) => location.id === form.locationId);
    if (!stillAvailable) {
      setForm((current) => ({ ...current, locationId: '' }));
    }
  }, [availableLocations, form.locationId]);

  if (!open) {
    return null;
  }

  const selectedEquipment = reservation
    ? equipmentById[reservation.equipmentId]
    : equipmentById[form.equipmentId];
  const selectedLocation = reservation
    ? locationById[reservation.locationId]
    : locationById[form.locationId];

  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setError('');
  };

  const handleSubmit = async () => {
    if (!form.equipmentId || !form.locationId || !form.borrowerName || !form.email || !form.phone || !form.preferredPickupDate) {
      setError('Please complete every field before placing the hold.');
      return;
    }

    try {
      await onSubmit({
        equipmentId: form.equipmentId,
        locationId: form.locationId,
        borrowerName: form.borrowerName.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        preferredPickupDate: form.preferredPickupDate,
      });
      setError('');
    } catch (submitError) {
      setError(submitError?.message || 'We could not save that reservation just now.');
    }
  };

  return (
    <div
      className="equipment-library__modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="equipment-library-modal-title"
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="equipment-library__modal-card">
        <button
          type="button"
          className="equipment-library__modal-close"
          aria-label="Close reservation window"
          onClick={onClose}
        >
          x
        </button>

        {reservation ? (
          <div className="equipment-library__confirmation">
            <div className="equipment-library__confirmation-chip">Reservation confirmed</div>
            <h3 id="equipment-library-modal-title" className="equipment-library__modal-title">
              Hold ready for pickup
            </h3>
            <p className="equipment-library__modal-copy">
              Bring the reservation ID or QR code to staff before closing on {formatDisplayDate(reservation.pickupDeadline)}.
            </p>

            <div className="equipment-library__confirmation-grid">
              <div className="equipment-library__confirmation-details">
                <div className="equipment-library__detail-row">
                  <span>Borrower</span>
                  <strong>{reservation.borrowerName}</strong>
                </div>
                <div className="equipment-library__detail-row">
                  <span>Equipment</span>
                  <strong>{selectedEquipment?.name || reservation.equipmentId}</strong>
                </div>
                <div className="equipment-library__detail-row">
                  <span>Pickup site</span>
                  <strong>{selectedLocation?.name || reservation.locationId}</strong>
                </div>
                <div className="equipment-library__detail-row">
                  <span>Pickup deadline</span>
                  <strong>{formatDisplayDate(reservation.pickupDeadline)}</strong>
                </div>
                <div className="equipment-library__detail-row">
                  <span>Reservation ID</span>
                  <strong>{reservation.id}</strong>
                </div>
              </div>

              <div className="equipment-library__confirmation-qr">
                <ReservationQrCode
                  value={reservation.qrCodeValue}
                  label="Equipment lending reservation QR placeholder"
                />
                <p className="equipment-library__qr-caption">
                  QR payload: {reservation.qrCodeValue}
                </p>
              </div>
            </div>

            <button
              type="button"
              className="equipment-library__primary-button"
              onClick={onClose}
            >
              Done
            </button>
          </div>
        ) : (
          <>
            <div className="equipment-library__confirmation-chip">Borrow a device</div>
            <h3 id="equipment-library-modal-title" className="equipment-library__modal-title">
              Reserve environmental testing equipment
            </h3>
            <p className="equipment-library__modal-copy">
              Choose a device, select a pickup location with current availability, and place a hold for community lending.
            </p>

            {error && <div className="equipment-library__error-banner">{error}</div>}

            <div className="equipment-library__form-grid">
              <label className="equipment-library__field">
                <span>Equipment type</span>
                <select
                  value={form.equipmentId}
                  onChange={(event) => updateField('equipmentId', event.target.value)}
                >
                  <option value="">Select equipment</option>
                  {equipmentCatalog.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </select>
              </label>

              <label className="equipment-library__field">
                <span>Pickup location</span>
                <select
                  value={form.locationId}
                  onChange={(event) => updateField('locationId', event.target.value)}
                  disabled={!form.equipmentId || availableLocations.length === 0}
                >
                  <option value="">
                    {form.equipmentId ? 'Select pickup site' : 'Choose equipment first'}
                  </option>
                  {availableLocations.map((location) => (
                    <option key={location.id} value={location.id}>
                      {location.name} ({location.availableCount} available)
                    </option>
                  ))}
                </select>
              </label>

              <label className="equipment-library__field">
                <span>Borrower name</span>
                <input
                  type="text"
                  value={form.borrowerName}
                  onChange={(event) => updateField('borrowerName', event.target.value)}
                  placeholder="Full name"
                />
              </label>

              <label className="equipment-library__field">
                <span>Email</span>
                <input
                  type="email"
                  value={form.email}
                  onChange={(event) => updateField('email', event.target.value)}
                  placeholder="name@example.com"
                />
              </label>

              <label className="equipment-library__field">
                <span>Phone number</span>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(event) => updateField('phone', event.target.value)}
                  placeholder="(555) 555-5555"
                />
              </label>

              <label className="equipment-library__field">
                <span>Preferred pickup date</span>
                <input
                  type="date"
                  value={form.preferredPickupDate}
                  min={getDefaultPickupDate()}
                  onChange={(event) => updateField('preferredPickupDate', event.target.value)}
                />
              </label>
            </div>

            {selectedEquipment && (
              <div className="equipment-library__selection-summary">
                <strong>{selectedEquipment.name}</strong>
                <p>{selectedEquipment.detects}</p>
                {selectedLocation ? (
                  <p>
                    Pickup site: {selectedLocation.name}
                  </p>
                ) : availableLocations.length === 0 ? (
                  <p>No pickup locations currently have this item available.</p>
                ) : (
                  <p>{availableLocations.length} pickup site(s) currently available.</p>
                )}
              </div>
            )}

            <div className="equipment-library__modal-actions">
              <button
                type="button"
                className="equipment-library__secondary-button"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                type="button"
                className="equipment-library__primary-button"
                disabled={submitting}
                onClick={handleSubmit}
              >
                {submitting ? 'Saving hold...' : 'Place hold'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
